import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useFlag } from 'flags';

import logo from 'static/images/logo.png';
import Menu from 'components/Menu/Menu';
import Icons from 'scenes/templateEditor/components/Icons';
import { pushNotificationWithTimeout } from 'modules/notifications/notificationsSlice';

import './MenuNav.scss';
import colors from 'theme/Colors.module.scss';

import { Box, Text, Tooltip } from '@chakra-ui/react';

import { defaultDates } from 'helpers/presetDateHelpers';

import { apiSlice } from 'app/apiSlice';
import { useLogoutMutation } from 'modules/app/appApi';
import { setDateRange, clearUser } from 'modules/app/appSlice';

const MenuNav = props => {
  const { setMobileMenu } = props;
  const isReadOnly = useFlag(['features', 'isReadOnly']);
  const dispatch = useDispatch();

  // Selectors
  const appMode = useSelector(state => state.app.appMode);
  const sessionUser = useSelector(state => state.app.user);

  const [logout] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logout().unwrap();

      // clear user state
      dispatch(clearUser());

      // clear all RTK Query cached data
      dispatch(apiSlice.util.resetApiState());

      //set initial date range
      dispatch(
        setDateRange({
          from: new Date(defaultDates.start),
          to: new Date(defaultDates.end),
        })
      );
      // TODO remove this hack to make per-session flags work
      window.localStorage.removeItem('FLAG_OVERRIDES'); // Clean localStorage
      dispatch(
        pushNotificationWithTimeout({
          type: 'success',
          message: 'Log out successfully',
        })
      );
    } catch (error) {
      dispatch(
        pushNotificationWithTimeout({
          type: 'error',
          message: 'Failed to log out.',
        })
      );
    }
  };

  const features = appMode => {
    const section = {
      title: null,
      menuItems: [
        {
          icon: Icons.Home,
          iconSize: 20,
          text: 'Dashboard',
          link: `${appMode === 'advertiser' ? '/adv/dashboard' : '/dashboard'}`,
          callback: () => setMobileMenu(false),
        },
        {
          icon: Icons.Analytics,
          iconSize: 20,
          text: 'Analytics',
          link: '/analytics',
          callback: () => setMobileMenu(false),
        },
        {
          icon: Icons.Campaigns,
          iconSize: 20,
          text: 'Campaigns',
          link: '/campaigns',
          callback: () => setMobileMenu(false),
        },
        {
          icon: Icons.Audiences,
          iconSize: 20,
          text: 'Audiences',
          link: '/audiences',
          callback: () => setMobileMenu(false),
        },
        {
          icon: Icons.Templates,
          iconSize: 20,
          text: 'Templates',
          link: '/templates',
          callback: () => setMobileMenu(false),
        },
      ],
    };

    // TODO: Enable inventory nav item when api is ready.
    // if (mode != 'advertiser') {
    //   section.menuItems.push({ icon: "fas fa-clipboard-list", text: "Inventory", link: "/inventory" })
    // }

    return section;
  };

  const settings = (id, isReadOnly, logout) => {
    const section = {
      title: null,
      menuItems: [
        {
          icon: Icons.MyAccount,
          iconSize: 20,
          text: 'My Account',
          link: '/user/me',
          callback: () => setMobileMenu(false),
        },
        {
          icon: Icons.ManageUsers,
          iconSize: 20,
          text: 'Manage Users',
          link: '/users',
          callback: () => setMobileMenu(false),
        },
        // {icon: "fas fa-user-circle", text: "Profile", link: "/profile"},
        // {icon: "fas fa-file-invoice-dollar", text: "Billing Info", link: "/billing-info"},
        // {icon: "fas fa-bell", text: "Notifications", link: "/notifications"},
      ],
    };

    if (!isReadOnly) {
      section.menuItems.push(
        {
          icon: Icons.OrderFields,
          iconSize: 20,
          text: 'Order Fields',
          link: '/companyParams',
          callback: () => setMobileMenu(false),
        },
        {
          icon: Icons.ManageFonts,
          iconSize: 20,
          text: 'Manage Fonts',
          link: '/fonts',
          callback: () => setMobileMenu(false),
        }
      );
    }
    section.menuItems.push({
      icon: Icons.Logout,
      iconSize: 20,
      text: 'Logout',
      link: '/logout',
      callback: handleLogout,
    });
    return section;
  };

  const buildMenu = () => {
    return (
      <Box className="menu-nav__content">
        <Menu content={features(appMode)} />
        <Menu content={settings(sessionUser?.id, isReadOnly, logout)} />
      </Box>
    );
  };

  return (
    <>
      <Box className="menu-nav__logo-container">
        <Link to="/" className="menu-nav__logo-link">
          <img className="menu-nav__logo" src={logo} alt="UnDigital Logo." />
        </Link>
        <Box className="menu-nav__personal-info">
          <Box color={colors.neutral700} className="personal-info__full-name">
            {sessionUser?.firstname} {sessionUser?.lastname}
          </Box>
          <Box className="personal-info__work-mail">
            <Tooltip
              bg={colors.white}
              label={
                <Text
                  as="span"
                  className="tooltip__label"
                  textColor={colors.neutral700}
                  fontWeight="400"
                >
                  {sessionUser?.email}
                </Text>
              }
              aria-label="A tooltip"
              placement="right"
            >
              <Text as="span">{sessionUser?.email} </Text>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      {buildMenu()}
    </>
  );
};

export default MenuNav;
